import type { CSSProperties } from 'react'
import type {
  DraftMetadataFileEntry,
  RubroRow,
  SkillOption
} from '../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotsHavePages,
  isRubroFallbackOtrosKind,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  rubroHasValidSelectedSkill
} from '../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'

export const PUBLISHED_RUBRO_EDIT_HINT =
  'Para editar rubros o datos de la obra publicada, usá «Ir a la obra» y gestioná todo desde la ficha.'

export function rubroDocumentationSectionIntroText(sourceFileLabel?: string | null): string {
  const label = String(sourceFileLabel || '').trim()
  if (!label) {
    return 'Revisá en cada pestaña lo que detectamos o generamos. Si algo no coincide, usá Quitar y elegí otro origen en el PDF u otro archivo, o dejalo vacío.'
  }
  return `Revisá en cada pestaña lo que detectamos o generamos a partir del documento «${label}». Si algo no coincide, usá Quitar y elegí otro origen en el PDF u otro archivo, o dejalo vacío.`
}

export function rubroSourceFileLabel(
  rubro: Pick<RubroRow, 'sourceFileId'>,
  metadataFiles: DraftMetadataFileEntry[]
): string | null {
  const sid = String(rubro.sourceFileId || '').trim()
  if (!sid) return null
  const f = metadataFiles.find((x) => String(x.id ?? '') === sid)
  return String(f?.name || f?.originalName || '').trim() || sid
}

export const PENDING_INPUT_STYLE: CSSProperties = {
  borderColor: '#cbd5e1',
  boxShadow: 'inset 0 0 0 2px #ea580c',
  borderRadius: 10
}

export function rubroKey(sp: RubroRow, idx: number): string {
  return sp.tempId || `sp-${idx}`
}

export function findRubroRowIndex(rows: RubroRow[], targetId: string): number {
  const byTempId = rows.findIndex((r) => r.tempId === targetId)
  if (byTempId >= 0) return byTempId
  for (let idx = 0; idx < rows.length; idx++) {
    if (`sp-${idx}` === targetId || `rubro-${idx}` === targetId) return idx
  }
  return -1
}

export function isRubroFuzzyKind(kind?: string): boolean {
  return String(kind || '').toLowerCase().includes('fuzzy')
}

export function isRubroUnresolvedKind(kind?: string): boolean {
  return String(kind || '').toLowerCase() === 'unresolved'
}

export function rubroHasDocumentationToReset(r: RubroRow): boolean {
  const slots = normalizeDocumentationSlots(r.documentationSlots ?? {})
  if (documentationSlotsHavePages(slots)) return true
  if (Array.isArray(r.sourcePages) && r.sourcePages.length > 0) return true
  const ids = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  return DOCUMENTATION_SLOT_KEYS.some((k) => {
    const v = ids[k]
    return typeof v === 'string' && v.trim().length > 0
  })
}

export function rubroPrimaryDisplayName(sp: RubroRow, skillOptions: SkillOption[]): string {
  if (rubroHasValidSelectedSkill(sp)) {
    const byId = skillOptions.find((s) => s.id === sp.selectedSkillId)
    if (byId) return byId.name
  }
  const matched = sp.matchedSkillName?.trim()
  if (matched) return matched
  const detected = sp.detectedLabel?.trim()
  if (detected) return detected
  return ''
}

export function rubroHeadline(sp: RubroRow, skillOptions: SkillOption[]): string {
  if (isRubroFallbackOtrosKind(sp.skillMatchKind)) {
    const n = rubroPrimaryDisplayName(sp, skillOptions)
    if (n) return `${n} — revisá el rubro`
    return 'Revisá el rubro'
  }
  const name = rubroPrimaryDisplayName(sp, skillOptions)
  if (name) return name
  return 'Elegí el rubro a cotizar'
}

export function rubroFallbackOtrosReview(sp: RubroRow): boolean {
  return isRubroFallbackOtrosKind(sp.skillMatchKind)
}

export function rubroUnresolvedNeedsPick(sp: RubroRow): boolean {
  return isRubroUnresolvedKind(sp.skillMatchKind) && !rubroHasValidSelectedSkill(sp)
}
